import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, isRef as _isRef, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, mergeProps as _mergeProps, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment } from "vue"
import _imports_0 from '@/assets/LogoManagement.svg'


const _hoisted_1 = { class: "flex flex-col gap-2" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-center text-color"
}
const _hoisted_3 = { class: "font-bold" }
const _hoisted_4 = { class: "italic" }
const _hoisted_5 = {
  key: 1,
  class: "flex w-full flex-col gap-1 px-1"
}
const _hoisted_6 = {
  key: 0,
  class: "mb-1 flex items-center gap-1 text-xs text-sky-600"
}
const _hoisted_7 = { class: "flex min-h-0 w-full flex-1 flex-col gap-4 overflow-hidden overflow-y-auto" }
const _hoisted_8 = ["href", "onClick"]
const _hoisted_9 = { class: "flex items-center gap-2" }
const _hoisted_10 = {
  key: 0,
  class: "bottom-2 flex flex-col items-center gap-4"
}
const _hoisted_11 = { class: "flex items-center justify-center gap-2" }
const _hoisted_12 = { class: "font-bold text-sky-800" }
const _hoisted_13 = { class: "font-bold text-sky-800" }
const _hoisted_14 = { class: "flex flex-col items-center justify-center gap-6" }
const _hoisted_15 = { class: "flex flex-row items-center justify-center gap-2" }
const _hoisted_16 = { class: "text-sm font-bold text-color" }
const _hoisted_17 = { class: "flex flex-col items-center gap-4" }
const _hoisted_18 = { class: "flex h-16 w-16 items-center justify-center rounded-full bg-sky-100" }
const _hoisted_19 = { class: "text-center" }
const _hoisted_20 = { class: "text-xl font-bold text-gray-800 dark:text-slate-50" }
const _hoisted_21 = { class: "flex flex-col gap-3" }
const _hoisted_22 = { class: "flex items-center gap-3" }
const _hoisted_23 = { class: "text-xs font-medium text-gray-500 dark:text-gray-400" }
const _hoisted_24 = { class: "text-sm text-gray-800 dark:text-slate-50" }
const _hoisted_25 = { class: "flex items-center gap-3" }
const _hoisted_26 = { class: "text-xs font-medium text-gray-500 dark:text-gray-400" }
const _hoisted_27 = { class: "text-sm text-gray-800 dark:text-slate-50" }
const _hoisted_28 = { class: "flex items-center gap-3" }
const _hoisted_29 = { class: "text-xs font-medium text-gray-500 dark:text-gray-400" }
const _hoisted_30 = { class: "text-sm text-gray-800 dark:text-slate-50" }

import ThemeSwitcher from '@/components/ThemeSwitcher .vue'
import Icons from '@/components/subcomponents/Icons.vue'
import UserUpdateModal from '@/components/userControl/UserUpdateModal.vue'
import { hasPermission } from '@/composables/RequiredPermissions'
import { useDarkMode } from '@/composables/useDarkMode'
import { buildUserDto } from '@/composables/useUserDtoBuilder'
import { UserPermission } from '@/models/enums/permissions'
import { INavBarItem } from '@/models/navbar'
import { IUser, getUserRoleLabel } from '@/models/user'
import { getAppVersion } from '@/services/appService'
import * as ls from '@/services/localStorage'
import * as userClientService from '@/services/userClientService'
import * as userService from '@/services/userService'
import { getBackendVersion } from '@/services/versionService'
import { useClientStore } from '@/stores/ClientStore'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useNavBarStore } from '@/stores/NavBarStore'
import { LogOut, PanelLeftOpen, UserRound } from 'lucide-vue-next'
import { Button, Dialog, Divider, PanelMenu, Tag, useToast } from 'primevue'
import type { MenuItem } from 'primevue/menuitem'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import SelectLanguage from '../subcomponents/SelectLanguage.vue'
import ClientSelect from './ClientSelect.vue'
import NavBarRail from './NavBarRail.vue'

type NavBarMenuItem = MenuItem & {
  multiline?: boolean
  items?: NavBarMenuItem[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'NavBar',
  setup(__props) {

const router = useRouter()

const navBar = useNavBarStore()

const languageStore = useLanguageStore()
const clientStore = useClientStore()

const isChangingClient = ref(false)
const selectedClientId = ref<string | null>(clientStore.currentClientId)

watch(
  () => clientStore.currentClientId,
  (newId) => {
    selectedClientId.value = newId
  },
)

async function onClientChange() {
  const id = selectedClientId.value
  if (!id || id === clientStore.currentClientId) return

  isChangingClient.value = true
  try {
    await userClientService.switchClient(id)
    clientStore.setCurrentClient(id)
  } catch {
    selectedClientId.value = clientStore.currentClientId
    await clientStore.loadClientsFromApi()
  } finally {
    isChangingClient.value = false
  }
}

const toast = useToast()
const showProfileDialog = ref(false)
const showProfileEditModal = ref(false)
const isUpdatingProfile = ref(false)
const currentUser = ref<IUser>({})

const loadCurrentUser = () => {
  const storedUser = ls.get(process.env.VUE_APP_LS_USER_LOGGED_KEY)
  currentUser.value = storedUser ? { ...storedUser, updatedAt: new Date(storedUser.updatedAt) } : {}
}

const openProfileEdit = () => {
  showProfileDialog.value = false
  showProfileEditModal.value = true
}

const handleProfileUpdate = async (formData: any) => {
  isUpdatingProfile.value = true
  try {
    const dto = buildUserDto(formData, currentUser.value)
    const res = await userService.updateSelf(dto)
    currentUser.value = res.data
    ls.set(process.env.VUE_APP_LS_USER_LOGGED_KEY, res.data)
    toast.add({
      severity: 'success',
      summary: languageStore.t('general.myAccount'),
      detail: languageStore.t('general.profileUpdated'),
      life: 3000,
    })
    showProfileEditModal.value = false
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: languageStore.t('general.myAccount'),
      detail: error?.response?.data?.detail ?? languageStore.t('general.errorUpdatingProfile'),
      life: 3000,
    })
  } finally {
    isUpdatingProfile.value = false
  }
}

onMounted(() => {
  clientStore.loadClientsFromApi()
  loadCurrentUser()
})

const { isDark } = useDarkMode()

const hasChildren = (item: NavBarMenuItem) => Boolean(item.items && item.items.length > 0)
const isMultilineItem = (item: NavBarMenuItem) => Boolean(item.multiline)

const permissionsNavbarRouter = computed(() => {
  const filterRoutes = (routes: INavBarItem[]): INavBarItem[] => {
    return routes

      .map((route): INavBarItem | null => {
        const routeCopy = { ...route }

        if (route.items) {
          const filteredItems: INavBarItem[] = filterRoutes(route.items)

          routeCopy.items = filteredItems

          return filteredItems.length > 0 ? routeCopy : null
        }

        if (!route.hasOwnProperty('requiredPermission')) {
          return routeCopy
        }

        return route.requiredPermission && hasPermission(route.requiredPermission as UserPermission)
          ? routeCopy
          : null
      })
      .filter((item): item is INavBarItem => item !== null)
  }

  return filterRoutes(navBar.routes)
})

const logout = () => userService.logout(router)
const userName = userService.getUserName()
const appVersion = getAppVersion()
const apiVersionLabel = ref('…')

onMounted(() => {
  void (async () => {
    try {
      const { data } = await getBackendVersion()
      apiVersionLabel.value = data.version
    } catch {
      apiVersionLabel.value = '—'
    }
  })()
})

const menuClasses = computed(() => [
  navBar.shown ? 'w-[20rem] grid grid-rows-[auto_1fr_auto]' : 'w-[4rem] flex flex-col',
  'bg-surface-0 dark:bg-surface-900 border-surface gap-4 fixed left-0 top-0 z-50 h-screen border-r p-2 shadow-lg transition-all duration-300',
])

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass(menuClasses.value)
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          class: _normalizeClass([
          'flex p-2',
          _unref(navBar).shown
            ? 'flex-row items-center justify-between gap-2'
            : 'flex-col-reverse items-center gap-2',
        ])
        }, [
          _createVNode(ThemeSwitcher, {
            modelValue: _unref(isDark),
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(isDark) ? (isDark).value = $event : null))
          }, null, 8, ["modelValue"]),
          _createVNode(_unref(PanelLeftOpen), {
            class: "cursor-pointer rounded-sm text-[var(--p-primary-color)] hover:bg-surface-100 dark:text-[var(--p-primary-color)] dark:hover:bg-surface-800",
            onClick: _unref(navBar).toggle
          }, null, 8, ["onClick"])
        ], 2),
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "flex w-full items-center justify-center py-4" }, [
          _createElementVNode("img", {
            src: _imports_0,
            class: "w-32",
            alt: "logo"
          })
        ], -1)),
        (_unref(navBar).shown)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("span", _hoisted_3, [
                _createTextVNode(_toDisplayString(_unref(languageStore).t('general.greeting')) + " ", 1),
                _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(userName)) + "!", 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (_unref(navBar).shown)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (isChangingClient.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                    _cache[5] || (_cache[5] = _createElementVNode("i", { class: "pi pi-spin pi-spinner" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(_unref(languageStore).t('general.changingClient')), 1)
                  ]))
                : _createCommentVNode("", true),
              _createVNode(ClientSelect, {
                modelValue: selectedClientId.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedClientId).value = $event)),
                disabled: isChangingClient.value,
                onChange: onClientChange
              }, null, 8, ["modelValue", "disabled"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_7, [
        (!_unref(navBar).shown)
          ? (_openBlock(), _createBlock(NavBarRail, {
              key: 0,
              model: permissionsNavbarRouter.value
            }, null, 8, ["model"]))
          : (_openBlock(), _createBlock(_unref(PanelMenu), {
              key: 1,
              model: permissionsNavbarRouter.value
            }, {
              item: _withCtx(({ item, props }) => [
                (item.to)
                  ? (_openBlock(), _createBlock(_component_router_link, {
                      key: 0,
                      to: item.to,
                      custom: ""
                    }, {
                      default: _withCtx(({ href, navigate }) => [
                        _createElementVNode("a", _mergeProps({ href: href }, props.action, {
                          onClick: navigate,
                          class: ["flex items-center gap-2", isMultilineItem(item) ? 'min-h-10 py-2' : 'h-10']
                        }), [
                          _createVNode(Icons, {
                            icon: item.icon,
                            class: "shrink-0 font-bold"
                          }, null, 8, ["icon"]),
                          _createElementVNode("span", {
                            class: _normalizeClass(["font-bold", isMultilineItem(item) ? 'whitespace-normal break-words leading-snug' : ''])
                          }, _toDisplayString(_unref(languageStore).t(item.label as string)), 3)
                        ], 16, _hoisted_8)
                      ]),
                      _: 2
                    }, 1032, ["to"]))
                  : (_openBlock(), _createElementBlock("a", _mergeProps({ key: 1 }, props.action, {
                      class: ["flex items-center justify-between gap-2", isMultilineItem(item) ? 'min-h-10 py-2' : 'h-10']
                    }), [
                      _createElementVNode("div", _hoisted_9, [
                        _createVNode(Icons, {
                          icon: item.icon,
                          class: "shrink-0 font-bold"
                        }, null, 8, ["icon"]),
                        _createElementVNode("span", {
                          class: _normalizeClass(["font-bold", isMultilineItem(item) ? 'whitespace-normal break-words leading-snug' : ''])
                        }, _toDisplayString(_unref(languageStore).t(item.label as string)), 3)
                      ]),
                      (hasChildren(item))
                        ? (_openBlock(), _createBlock(Icons, {
                            key: 0,
                            icon: "chevron-down",
                            class: "shrink-0 font-bold"
                          }))
                        : _createCommentVNode("", true)
                    ], 16))
              ]),
              _: 1
            }, 8, ["model"]))
      ]),
      (_unref(navBar).shown)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", {
                class: "flex cursor-pointer items-center gap-2 rounded-md bg-sky-100 px-3 py-2 transition-colors hover:bg-sky-200",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (showProfileDialog.value = true))
              }, [
                _createVNode(_unref(UserRound), {
                  size: 16,
                  "stroke-width": 2.5,
                  color: "#03619C"
                }),
                _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(languageStore).t('general.myAccount')), 1)
              ]),
              _createElementVNode("div", {
                class: "flex cursor-pointer items-center gap-2 rounded-md bg-sky-100 px-3 py-2 transition-colors hover:bg-sky-200",
                onClick: logout
              }, [
                _createVNode(_unref(LogOut), {
                  size: 16,
                  "stroke-width": 2.5,
                  color: "#03619C"
                }),
                _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(languageStore).t('general.exit')), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createVNode(SelectLanguage, { class: "mx-2" }),
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("span", _hoisted_16, "Version: " + _toDisplayString(_unref(appVersion)), 1)
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ], 2),
    _createVNode(_unref(Dialog), {
      visible: showProfileDialog.value,
      "onUpdate:visible": _cache[3] || (_cache[3] = ($event: any) => ((showProfileDialog).value = $event)),
      header: _unref(languageStore).t('general.myAccount'),
      modal: "",
      class: "!w-[90vw] lg:!w-[28rem]"
    }, {
      footer: _withCtx(() => [
        _createVNode(_unref(Button), {
          label: _unref(languageStore).t('general.edit'),
          icon: "pi pi-pencil",
          variant: "outlined",
          onClick: openProfileEdit
        }, null, 8, ["label"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createVNode(_unref(UserRound), {
              size: 32,
              "stroke-width": 1.5,
              color: "#03619C"
            })
          ]),
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("p", _hoisted_20, _toDisplayString(currentUser.value?.name), 1),
            _createVNode(_unref(Tag), {
              severity: "info",
              value: _unref(getUserRoleLabel)(currentUser.value?.userRole),
              class: "mt-1"
            }, null, 8, ["value"])
          ])
        ]),
        _createVNode(_unref(Divider)),
        _createElementVNode("div", _hoisted_21, [
          _createElementVNode("div", _hoisted_22, [
            _cache[7] || (_cache[7] = _createElementVNode("i", { class: "pi pi-envelope text-sky-700" }, null, -1)),
            _createElementVNode("div", null, [
              _createElementVNode("p", _hoisted_23, _toDisplayString(_unref(languageStore).t('general.email')), 1),
              _createElementVNode("p", _hoisted_24, _toDisplayString(currentUser.value?.email || '—'), 1)
            ])
          ]),
          _createElementVNode("div", _hoisted_25, [
            _cache[8] || (_cache[8] = _createElementVNode("i", { class: "pi pi-info-circle text-sky-700" }, null, -1)),
            _createElementVNode("div", null, [
              _createElementVNode("p", _hoisted_26, _toDisplayString(_unref(languageStore).t('general.description')), 1),
              _createElementVNode("p", _hoisted_27, _toDisplayString(currentUser.value?.description || '—'), 1)
            ])
          ]),
          _createElementVNode("div", _hoisted_28, [
            _cache[9] || (_cache[9] = _createElementVNode("i", { class: "pi pi-bell text-sky-700" }, null, -1)),
            _createElementVNode("div", null, [
              _createElementVNode("p", _hoisted_29, _toDisplayString(_unref(languageStore).t('general.receiveEmails')), 1),
              _createElementVNode("p", _hoisted_30, _toDisplayString(currentUser.value?.receiveEmails
                ? _unref(languageStore).t('general.yes')
                : _unref(languageStore).t('general.no')), 1)
            ])
          ])
        ])
      ]),
      _: 1
    }, 8, ["visible", "header"]),
    (showProfileEditModal.value)
      ? (_openBlock(), _createBlock(UserUpdateModal, {
          key: 0,
          showModal: showProfileEditModal.value,
          user: currentUser.value,
          loading: isUpdatingProfile.value,
          "is-self-edit": true,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (showProfileEditModal.value = false)),
          onSave: handleProfileUpdate
        }, null, 8, ["showModal", "user", "loading"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})